// Real-user Web Vitals capture. Reports LCP / CLS / INP / FCP / TTFB
// to the `web_vitals` table for the /admin/web-vitals dashboard.
// Metrics are buffered and flushed via a keepalive fetch straight to the
// REST endpoint, so the heavy database client never loads on page view.
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";

type Device = "mobile" | "tablet" | "desktop";

const detectDevice = (): Device => {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
};

const SESSION_KEY = "agape_wv_session";
const getSessionId = (): string => {
  if (typeof sessionStorage === "undefined") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

type Row = {
  metric: string;
  value: number;
  rating: string;
  path: string;
  language: string | null;
  device: Device;
  navigation_type: string;
  session_id: string;
};

let buffer: Row[] = [];
let flushing = false;

const toRow = (metric: Metric): Row => ({
  metric: metric.name,
  // CLS is unitless; keep as-is. Others are ms.
  value: Number.isFinite(metric.value) ? metric.value : 0,
  rating: metric.rating,
  path: window.location.pathname.slice(0, 512),
  language: document.documentElement.lang || null,
  device: detectDevice(),
  navigation_type: metric.navigationType,
  session_id: getSessionId(),
});

const flush = () => {
  if (flushing || buffer.length === 0) return;
  flushing = true;
  const rows = buffer;
  buffer = [];
  const url = import.meta.env["VITE_SUPABASE_URL"];
  const key = import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  // Fire-and-forget; never break the page if the network drops.
  fetch(`${url}/rest/v1/web_vitals`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      apikey: key,
      Authorization: `Bearer ${key}`,
      Prefer: "return=minimal",
    },
    body: JSON.stringify(rows),
  })
    .catch((err) => {
      if (import.meta.env.DEV) console.warn("[web-vitals] insert failed", err);
    })
    .finally(() => {
      flushing = false;
    });
};

const collect = (metric: Metric) => {
  buffer.push(toRow(metric));
  scheduleFlush();
};

let flushTimer: number | undefined;
const scheduleFlush = () => {
  if (flushTimer !== undefined) return;
  // Flush shortly after the page settles — LCP arrives within the first
  // seconds; INP only after interaction, so pagehide is the real backstop.
  flushTimer = window.setTimeout(() => {
    flushTimer = undefined;
    flush();
  }, 8000);
};

let started = false;
export const initWebVitals = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  onLCP(collect);
  onCLS(collect);
  onINP(collect);
  onFCP(collect);
  onTTFB(collect);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);
};
